import { Badge } from "@vds/badges";
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Button, ButtonGroup, TextLink } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody, ModalFooter } from "@vds/modals";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { Notification } from "@vds/notifications";
import { Tooltip } from "@vds/tooltips";
import { Body, Title } from "@vds/typography";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWindowSize } from "../../../global/hooks/viewportHooks";
import {
  createSearchParams,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import DEFAULTFOVIMGE from "../../../assets/fov.png";
import { BasicSnapshot } from "../../../global/components/snapshots/BasicSnapshot";
import {
  CVAPP_CAMERAADJUSTMENT,
  CVAPP_ENTRY_EXIT_KEYWORD,
  CVAPP_OBJECTBLUR,
  CVAPP_PARKINGOCCUPANCYEE,
  CVAPP_PARKINGOCCUPANCYOA,
  CVAPP_PROXIMITY,
  CVAPP_TOOLTIP_PEOPLE_EE,
  CVAPP_TOOLTIP_PEOPLE_OA,
  CVAPP_TOOLTIP_VEHICLE_EE,
  CVAPP_TOOLTIP_VEHICLE_OA,
  CVAPP_UNOBJECTDETECTION,
  CVAPP_VEHICLE_KEYWORD,
} from "../../../global/constants/cvappConstants";
import { BUTTON_LABELS, CALIBRATION_MODES, NOTIFICATION_TYPES, SUBTITLES, TITLES } from "../../../constants/cameraInfoConstants";
import { getActiveLicenseSku, isV2Sku, LICENSE_LABELS, LICENSE_MODAL_TEXTS, LICENSE_SKU_PATTERNS, LICENSE_TYPES, LICENSE_VALUES, licensesMessage, MAX_APP_LIMIT_MODAL, ORIGIN_PAGES } from "../../../global/constants/licensesConstants";
import { arrangeAsAlphabeticalOrder } from "../../../global/utility/statisticsUtil";
import { getLicenseTypeDisplayed } from "../../../global/utility/Utils";
import { snapshotImage, StreamType } from "../../../hoc/snapshotImage";
import { changeLicenseType, clearAdvancedConfigAppearances, resetRoi, saveStreamImageMapping, setSelectedCameraQR, showCalibrationBanner } from "../../../store/actions";
import BaseModal from "../../../global/components/Modals/BaseModal";
import ChangeLicenseModal from "../../../global/components/Modals/ChangeLicenseModal";
import { CAMERA_ONBOARDING_TEST_IDS } from "../cameraOnboardingConstants";
import { isCcdHasInHouseVersion } from "../../../global/utility/devicesUtils";
import StreamCalibrationModal from "../../../features/camerOnboarding/discoveredCameraOnboarding/components/StreamCalibrationModal";
import "./cvAppsSub.css";
import { STORAGE } from "../../../global/constants/storageConstants";
import ErrorTemplate from "../../../global/components/errorTemplate/ErrorTemplate";

let nameSet = new Set();
let cachedCvAppSubState = null; // Module-level cache: preserves router state across renders where location.state is null (e.g. browser back/forward, direct URL access)
const CVAppSub = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const buttonSize = width <= 767 ? "small" : "large";
  const { state: routerState } = useLocation(); // present only on programmatic navigation (navigate with state); null on direct/back nav → falls back to cache
  if (routerState) cachedCvAppSubState = routerState;
  const state = routerState ?? cachedCvAppSubState;
  const bannerInfo = useSelector(
    (state) => state.notificationBanner.bannerInfo
  );
  const [loading, setLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [fovS, setFOVs] = useState([
    "Checkout desk 1",
    "Checkout desk 2",
    "Computer desks",
    "Bookshelves",
  ]);
  const [selectedOptions, setSelectedOptions] = useState(
    fovS.reduce((acc, title) => {
      acc[title] = [];
      return acc;
    }, {})
  );
  const ccdList = useSelector((state) => state.deviceReducer.companionDeviceList);
  const cameraList = useSelector((state) => state.deviceReducer.cameraList);
  const [selectedCvAppid, setSelectedCvAppid] = useState([]);
  const [uniqueDisplayNames, setUniqueDisplayNames] = useState([]);
  const [checkedCvAppid, setCheckedCvAppid] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedCvAppName, setSelectedCvAppName] = useState([]);
  const [selectedSubCategoryNames, setSelectedSubCategoryNames] = useState([]);
  const [showLicenseLimitBanner, setShowLicenseLimitBanner] = useState(false);
  const [isNextDisable, setIsNextDisable] = useState(false);
  const [fovsArrImg, setfovArrImg] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCVAppsWithCount, setSelectedCVAppsWithCount] = useState([]);
  const [pendingLimitApp, setPendingLimitApp] = useState(null);
  const [currentSku, setCurrentSku] = useState(state?.sku);
  const [requestedSku, setRequestedSku] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const roiListFromRedux = useSelector(
    (reduxState) => reduxState.polygonRoiReducer.roiListWithCvapps
  );
  const fovIdForFilter = Array.isArray(state?.FOVId) ? state?.FOVId[0] : state?.FOVId;
  const cvAppList = useMemo(() => {
    const fromRedux = roiListFromRedux?.filter((f) => f?.parentdeviceid == fovIdForFilter);
    return fromRedux?.length > 0 ? fromRedux : state?.roiList;
  }, [roiListFromRedux, fovIdForFilter]);

  const cameraName = state?.cameraname;
  const camInfo = useMemo(() => 
    state?.camInfo || state?.data || cameraList.find(c => c.id === state?.cameraId),
   [state?.camInfo, state?.data, cameraList, state?.cameraId]
  );
  const cameraParentCCDInfo = ccdList.find(ccd => ccd?.id === camInfo?.parentdeviceid) ;
  const [isFetchingPairedImaged, setIsFetchingPairedImaged] = useState("");
  const cvAppsFromReducer = useSelector((state) => state.cvAppReducer.cvAppList);
  const targetVersion = isCcdHasInHouseVersion(cameraParentCCDInfo) ? "2" : "1";
  const cvAppListFromReducer = (cvAppsFromReducer ?? []).filter((cvapp) => cvapp.version === targetVersion);
  const fovList = useSelector((state) => state.deviceReducer.fovList);
  const roiList = useSelector(
    (state) => state.polygonRoiReducer.roiListWithCvapps
  );
  const imageReducerList = useSelector(
    (state) => state.imagesReducer.streamImages
  );
  const fovListToDisplay = fovList?.filter(
    (f) => state?.FOVId?.indexOf(f.id) > -1
  );
  const isInHouse = Boolean(isCcdHasInHouseVersion(cameraParentCCDInfo));
  const licenses = useSelector((state) => state.licenseReducer.licenses);
  const licenseList = useMemo(() => {
    return (licenses || []).filter((license) => {
      if (!license?.sku) return false;
      return isV2Sku(license.sku) === isInHouse;
    });
  }, [licenses, isInHouse]);
  const { changeLicenseTypeSuccess, changeLicenseTypeFailure } = useSelector((state) => state.licenseReducer);
  const billingAccount = activeSession?.accountId || "";
  const deviceId = Array.isArray(state?.FOVId) ? state?.FOVId[0] : state?.FOVId;
  const [licenseTypeCountLimit, setLicenseTypeCountLimit] = useState(3);
  const [licenseStartedCount, setLicenseStartedCount] = useState(0);
  const [SnapshotImg, setSnapshotImg] = useState(null);
  const [floorSnapshotImage, setFloorSnapshotImage] = useState("");
  const [isChangeLicenseModalVisible, setIsChangeLicenseModalVisible] = useState(false);
  const [showCalibrationConfirmModal, setShowCalibrationConfirmModal] = useState(false);
  const [pendingCalibrationApp, setPendingCalibrationApp] = useState(null);
  const [showStreamCalibrationModal, setShowStreamCalibrationModal] = useState(false);
  const [showManualCalibration, setShowManualCalibration] = useState(false);
  const [handleNextCalibrationSteps, setHandleNextCalibrationSteps] = useState(false);
  const [manualCalibrationCount, setManualCalibrationCount] = useState(0);
  const [manualCalibrationDevices, setManualCalibrationDevices] = useState([]);
  const [showManualCalibrationBack, setShowManualCalibrationBack] = useState(false);
  const [isCalibrationLoading, setIsCalibrationLoading] = useState(false);
  const [isCalibrationNextDisabled, setIsCalibrationNextDisabled] = useState(true);
  const calibrationBannerInfo = useSelector((state) => state.calibrationBanner?.bannerInfo);
  const [pendingCalibrationFov, setPendingCalibrationFov] = useState(null);
  const updateSnapshot = (url) => {
    setFloorSnapshotImage(url);
  };
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);

  // set cvApps associated with the fov
  useEffect(() => {
    nameSet = new Set();
    const appsList = [];
    cvAppList?.forEach((item) => {
      if (item.cvapps && Array.isArray(item.cvapps)) {
        item.cvapps.forEach((cvapp) => {
          const displayName =
            cvapp?.resources?.displayNameFull ||
            cvapp?.resources?.displayName ||
            cvapp?.name ||
            cvapp?.subcategory;
          if (displayName) {
            nameSet.add(displayName);
          }
          appsList.push(displayName);
        });
      }
    });
    const cvApps = Array.from(nameSet);
    setCheckedCvAppid(cvApps);
    setSelectedNames(cvApps);
    setSelectedCVAppsWithCount(appsList);
    setLicenseStartedCount(cvApps?.length);
    if (cvApps?.length < licenseTypeCountLimit) {
      setShowModal(false);
    }
  }, [cvAppList]);

  // Clear appearances when component mounts
  useEffect(() => {
    dispatch(clearAdvancedConfigAppearances());
  }, []);

  useEffect(
    (streamId, imageUrl) => {
      var dImage = {};
      const fovIdDetails = fovList.filter((obj) => obj.id === streamId);
      fovsArrImg.push({
        name: fovIdDetails[0]?.name,
        url: imageUrl,
        fovId: streamId,
      });
      setfovArrImg(fovsArrImg);
      if (Array.isArray(state?.FOVId)) {
        state?.FOVId.map((item) => {
          dImage = imageReducerList.find((f) => f.id == state?.FOVId);
          if (dImage != undefined) {
            dImage["imageUrl"] =
              dImage.imageUrl != undefined ? dImage.imageUrl : DEFAULTFOVIMGE;
          }
        });
      } else {
        dImage = imageReducerList.find((f) => f.id == state?.FOVId);
        const newSnap = snapshotImage(
          BasicSnapshot,
          state?.FOVId,
          state?.cameraId,
          StreamType.PAIRED_CAMERA,
          "",
          updateSnapshot
        );
        setSnapshotImg(() => newSnap);
      }
    },
    [state?.FOVId]
  );

  useEffect(() => {
    if (bannerInfo.showNotification === false) {
      setLoading(false);
    }
  }, [bannerInfo]);

  useEffect(() => {
    handleFovs();
  }, [state?.fieldOfViews]);

  const handleFovs = () => {
    let fovsArr = [];
    if (state && Object.keys(state)?.length && state?.fieldOfViews) {
      if (Array.isArray(state.fieldOfViews)) {
        state?.FOVNames?.map((item, index) => {
          if (item) {
            fovsArr.push(item);
          } else {
            fovsArr.push(state.fieldOfViews[index].fovName);
          }
        });
      } else {
        fovsArr.push(state?.FOVNames);
      }
    } else {
      fovsArr.push(state?.data?.name);
    }
    setFOVs(fovsArr);
  };

  useEffect(() => {
    if (showLicenseLimitBanner) {
      setShowModal(false);
      setTimeout(() => {
        setShowModal(true);
        setShowLicenseLimitBanner(false);
      }, 100);
    }
  }, [showLicenseLimitBanner]);

  useEffect(() => {
    if (changeLicenseTypeSuccess) {
      setCurrentSku(requestedSku);
      setRequestedSku(null);
      setLicenseStartedCount(checkedCvAppid?.length ?? 0);
      setIsUpgrading(false);
      setShowUpgradeSuccess(true);
      dispatch({ type: "RESET_CHANGE_LICENSE_TYPE" });
    }
  }, [changeLicenseTypeSuccess]);

  useEffect(() => {
    if (changeLicenseTypeFailure) {
      setRequestedSku(null);
      setIsUpgrading(false);
      dispatch({ type: "RESET_CHANGE_LICENSE_TYPE" });
    }
  }, [changeLicenseTypeFailure]);

  useEffect(() => {
    if (!deviceId || !fovList?.length) return;
    const fov = fovList.find((f) => f?.id === deviceId);
    const skuFromRedux = getActiveLicenseSku(fov?.licenses);
    if (skuFromRedux) {
      setCurrentSku(skuFromRedux);
      if (state?.sku && skuFromRedux !== state.sku) {
        setLicenseStartedCount(0);
      }
    }
  }, []);

  // The in-progress banner stays (non-dismissible) until Refresh confirms the calibration finished.
  const isCalibrationInProgress = (fov) =>
    Boolean(fov) &&
    calibrationBannerInfo?.showNotification &&
    calibrationBannerInfo?.type === NOTIFICATION_TYPES.STREAM_CALIBRATION &&
    calibrationBannerInfo?.isDismissible === false &&
    calibrationBannerInfo?.deviceId === fov.parentdeviceid;

  const handleOptionChange = (
    title,
    event,
    option,
    category,
    floorImage,
    sku,
    licenseList
  ) => {
    const isChecked = event.target.checked;
    const name = option?.name;
    if (event.target.checked) {
      const isStarter = getLicenseTypeDisplayed(sku, licenseList) === LICENSE_TYPES.STARTER;
      const isExtraFeatureApp = name === CVAPP_OBJECTBLUR || name === CVAPP_CAMERAADJUSTMENT;
      const limitCount = licenseStartedCount + 1;

      // Apps requiring manual calibration — only enforced on v2 licensed streams;
      const isV2Stream = isV2Sku(sku);
      const requiresCalibration = name === CVAPP_UNOBJECTDETECTION || name === CVAPP_PROXIMITY;
      const fovForCalibration = fovListToDisplay?.find((f) => f.name == title);
      const isAutomaticCalibration =
        !fovForCalibration?.fields?.calibration?.mode ||
        fovForCalibration.fields.calibration.mode === CALIBRATION_MODES.AUTOMATIC;
      // A calibration already started for this stream: don't let it be triggered again.
      if (isV2Stream && requiresCalibration && isCalibrationInProgress(fovForCalibration)) {
        setPendingCalibrationApp({ fov: fovForCalibration, name, title, calibrationInProgress: true });
        setShowCalibrationConfirmModal(true);
        return;
      }
      if (isV2Stream && requiresCalibration && isAutomaticCalibration) {
        setPendingCalibrationApp({ fov: fovForCalibration, name, option, title, category, floorImage, sku });
        setShowCalibrationConfirmModal(true);
        return;
      }

      // Extra feature app on Starter — show upgrade modal only, never check the app
      if (isStarter && isExtraFeatureApp) {
        setPendingLimitApp({ name, option, title, sku });
        setIsChangeLicenseModalVisible(true);
        return;
      }

      // 4th app on Starter — show limit modal, do NOT check the app
      if (
        isStarter &&
        limitCount > 3 &&
        !selectedCVAppsWithCount.includes(option?.displayName) &&
        !selectedCVAppsWithCount.includes(option?.name)
      ) {
        setLicenseTypeCountLimit(3);
        setShowLicenseLimitBanner(true);
        setPendingLimitApp({ name, option, title, sku });
        return;
      }

      if (
        (isStarter &&
          limitCount >= 3 &&
          !selectedCVAppsWithCount.includes(option?.displayName) &&
          !selectedCVAppsWithCount.includes(option?.name)) ||
        (!isStarter &&
          selectedCvAppName.length >= 30)
      ) {
        setLicenseTypeCountLimit(
          isStarter ? 3 : 30
        );
      }
      if (
        isStarter &&
        !selectedCVAppsWithCount.includes(option?.displayName) &&
        !selectedCVAppsWithCount.includes(option?.name)
      ) {
        setLicenseStartedCount(limitCount);
      }
      setSelectedCvAppName([...selectedCvAppName, name]);
    } else {
      selectedCvAppName.splice(selectedCvAppName.indexOf(name), 1);
      setSelectedCvAppName(selectedCvAppName);
      if (
        getLicenseTypeDisplayed(sku, licenseList) == LICENSE_TYPES.STARTER &&
        !selectedCVAppsWithCount.includes(option?.displayName) &&
        !selectedCVAppsWithCount.includes(option?.name)
      ) {
        setLicenseStartedCount(licenseStartedCount - 1);
      }
    }

    setSelectedNames((prevSelected) =>
      prevSelected.includes(name)
        ? prevSelected.filter((item) => item !== name)
        : [...prevSelected, name]
    );
    if (isChecked) {
      setSelectedOptions((prevSelectedOptions) => {
        if (prevSelectedOptions && prevSelectedOptions[title]) {
          const newOptions = [
            ...prevSelectedOptions[title],
            {
              name: option.name,
              id: option.id,
              category: category,
              floorImage: floorSnapshotImage,
            },
          ];
          return { ...prevSelectedOptions, [title]: newOptions };
        } else {
          const newOptions = [
            {
              name: option.name,
              id: option.id,
              category: category,
              floorImage: floorSnapshotImage,
            },
          ];
          return { [title]: newOptions };
        }
      });
    } else {
      const filterSelection =
        (selectedOptions[title] || []).filter((obj) => obj.id !== option.id);
      setSelectedOptions({ [title]: filterSelection });
    }
  };

  const handleNextButtonClick = (floorImage) => {
    const rois = roiList?.filter((f) => f?.parentdeviceid == state?.FOVId);
    var tempSelectedCvApp = [];
    //if block only work if cv app check bx selected before rendering of image and next button clicked after image rendered.
    if (isFetchingPairedImaged.indexOf("Updated at: ") > -1) {
      const getcvpappnamefromRois = rois?.map((r) => {
        return r?.cvapps?.map((f) => {
          return f.name;
        });
      });
      var doSelectCvappList = [];
      if (
        getcvpappnamefromRois != undefined &&
        getcvpappnamefromRois.length > 0
      ) {
        getcvpappnamefromRois.forEach((element) => {
          if (element != undefined) {
            element.forEach((k) => {
              doSelectCvappList.push(k);
            });
          }
        });
      }

      const justSelecteCvappnameList = selectedCvAppName.filter(
        (f) => doSelectCvappList.indexOf(f) == -1
      );
      tempSelectedCvApp = justSelecteCvappnameList.map((l) => {
        return {
          name: l,
          id: cvAppListFromReducer.find((c) => c.name == l)?.id,
          cvappid: cvAppListFromReducer.find((c) => c.name == l)?.id,
          category: cvAppListFromReducer.find((c) => c.name == l)?.category,
          floorImage: floorSnapshotImage,
          title: "stream_" + state?.FOVId,
          value: l,
        };
      });
    }

    const selectedCvApps = [];
    const selectedCvAppIds = [];
    Object.keys(selectedOptions).forEach(function (key, index) {
      selectedOptions[key]?.map((item) => {
        selectedCvApps.push({
          title: key,
          value: item?.name,
          cvappid: item?.id,
          ...item,
        });
      });
    });
    Object.keys(selectedCvAppid).forEach(function (key, index) {
      selectedCvAppid[key] &&
        selectedCvAppid[key].map((item) => {
          selectedCvAppIds.push({ title: key, value: item });
        });
    });
    dispatch(resetRoi());
    const selectedState = {
      cvapps: selectedCvApps?.length == 0 ? tempSelectedCvApp : selectedCvApps,
      cameraId: state.cameraId,
      companionId: state.companionId,
      fieldOfViews: state.fieldOfViews,
      FOVNames: state.FOVNames,
      FOVId: state.FOVId,
      action: "create",
      siteSelected: state?.siteSelected,
      floorSelected: state?.floorSelected,
      areaSelected: state?.areaSelected,
      subAreaSelected: state?.subAreaSelected,
      cameraname: state?.cameraname,
      cvappid: selectedCvAppIds,
      fromAction: state?.action,
      cameradata: state?.cameradata,
      roiList: cvAppList?.length == 0 ? rois : cvAppList,
      sku: currentSku,
      originPage: state?.originPage ? state?.originPage : "",
    };
    navigate("/polygon", { state: selectedState });
  };
  const handleBackButtonClick = () => {
    navigate(`/cameraInfoPage?cameraId=${state?.cameraId}`);
  };
  const close = () => {
    setShowModal(false);
    if (pendingLimitApp) {

      setPendingLimitApp(null);
    }
  };

  const navigateToUnsubscribeRoi = (streamId, unsubscribeFlow = true) => {
    const fovObject = fovList?.find((f) => f.id === streamId);
    const cameraObj = Array.isArray(state?.cameradata)
      ? state?.cameradata?.find((item) => item?.id === state?.cameraId)
      : state?.cameradata;
    navigate("/roi", {
      state: {
        data: cameraObj || camInfo,
        streamData: fovObject ? [fovObject] : [],
        FOVId: streamId,
        FOVSnapshot: fovObject?.customdata?.imageid,
        fieldOfViews: fovObject?.name,
        FOVName: fovObject?.name,
        cameraname: state?.cameraname,
        originPage: ORIGIN_PAGES.CV_APP_SUB,
        cvAppSubReturnState: state,
        isUnsubscribeFlow: unsubscribeFlow
      },
    });
  };

  const closeCalibrationConfirmModal = () => {
    setShowCalibrationConfirmModal(false);
    setPendingCalibrationApp(null);
  };

  const handleCalibrationConfirmYes = () => {
    const pendingFov = pendingCalibrationApp?.fov;
    setShowCalibrationConfirmModal(false);
    setPendingCalibrationApp(null);
    if (camInfo?.qrcode) {
      dispatch(setSelectedCameraQR(camInfo.qrcode));
    }
    setManualCalibrationCount(0);
    setManualCalibrationDevices([]);
    setShowManualCalibration(false);
    setIsCalibrationNextDisabled(true);
    setShowStreamCalibrationModal(true);
    setPendingCalibrationFov(pendingFov);
  };

  const closeStreamCalibrationModal = () => {
    setShowStreamCalibrationModal(false);
    setShowManualCalibration(false);
    setManualCalibrationCount(0);
    setManualCalibrationDevices([]);
    setIsCalibrationNextDisabled(true);
    setPendingCalibrationFov(null);
  };

  const handleCalibrationComplete = () => {
    const streamName = pendingCalibrationFov?.name;
    dispatch(showCalibrationBanner({
      type: NOTIFICATION_TYPES.STREAM_CALIBRATION,
      priority: "success",
      data: {
        title: streamName ? `${streamName} ${TITLES.STREAM_CALIBRATION_COMPLETE}` : TITLES.STREAM_CALIBRATION_COMPLETE,
        subtitle: SUBTITLES.STREAM_CALIBRATION_COMPLETE
      },
      showNotification: true
    }));
    closeStreamCalibrationModal();
  };

  const handleChangeLicenseConfirm = (selectedSku) => {
    setRequestedSku(selectedSku);
    setIsChangeLicenseModalVisible(false);
    setIsUpgrading(true);
    dispatch(changeLicenseType(billingAccount, deviceId, selectedSku, currentSku, null, fovList, licensesMessage.UPGRADE_LICENSE));
  };

  const getFovLicenseSku = (fovname) => {
    const tFov = fovListToDisplay.find((f) => f.name == fovname);
    return tFov?.licenses == undefined
      ? LICENSE_VALUES.NO_LICENSE
      : tFov?.licenses.length == 0
      ? LICENSE_VALUES.NO_LICENSE
      : getActiveLicenseSku(tFov?.licenses);
  };

  const disbale_NextButton = (isNextDisable) => {
    var difference = selectedCvAppName?.filter(
      (x) => !uniqueDisplayNames.includes(x)
    );
    const nextDisabled =
      licenseStartedCount >= 4 &&
      getLicenseTypeDisplayed(currentSku, licenseList) === LICENSE_TYPES.STARTER;
    if (nextDisabled) return true;
    if (difference.length > 0 || isNextDisable) {
      return fovsArrImg.length > 0 && fovsArrImg[0].url != DEFAULTFOVIMGE
        ? false
        : true;
    } else {
      return true;
    }
  };
  const calibrationFovDatas = useMemo(
    () => (pendingCalibrationFov ? [{ fovId: pendingCalibrationFov.id, fovName: pendingCalibrationFov.name }] : []),
    [pendingCalibrationFov]
  );

  return (
    <div className="cvappsection-container">
      {isUpgrading && <Loader active={true} fullscreen={false} />}
      {
        <Modal
          className="cvappsub-modal"
          opened={showModal}
          disableOutsideClick={true}
          hideCloseButton={true}
          closeButton={
            <div
              className="cvappsub-modal-close"
              data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPP_SUB_CLOSE_ICON_MODAL}
              onClick={() => {
                close();
              }}
            >
              <Icon name="close" />
            </div>
          }
        >
          <ModalBody>
            <Notification
              type="error"
              hideCloseButton={true}
              title={MAX_APP_LIMIT_MODAL.TITLE}
              subtitle={
                <span>
                  {MAX_APP_LIMIT_MODAL.CAPACITY_MESSAGE(licenseTypeCountLimit)}
                  <TextLink
                    kind="standalone"
                    size="large"
                    onClick={() => {
                      setShowModal(false);
                      setIsChangeLicenseModalVisible(true);
                    }}
                  >
                   {LICENSE_MODAL_TEXTS.UPGRADE_LICENSE}
                  </TextLink>
                  {checkedCvAppid?.length > 0 ? (
                    <>
                      {MAX_APP_LIMIT_MODAL.OR}
                      <TextLink
                        kind="standalone"
                        size="large"
                        onClick={() => {
                          setShowModal(false);
                          const streamId = Array.isArray(state?.FOVId) ? state?.FOVId[0] : state?.FOVId;
                          navigateToUnsubscribeRoi(streamId, true);
                        }}
                      >
                        {MAX_APP_LIMIT_MODAL.UNSUBSCRIBE_LINK}
                      </TextLink>
                      {MAX_APP_LIMIT_MODAL.FROM_EXISTING_APPS}
                    </>
                  ) : (
                    MAX_APP_LIMIT_MODAL.SELECT_MORE_SUFFIX
                  )}
                </span>
              }
            />
            <div style={{ display: "flex", justifyContent: "center", marginTop: "24px" }}>
              <Button
                use="secondary"
                width="200px"
                data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPP_SUB_CLOSE_ICON_MODAL}
                onClick={() => close()}
              >
                {MAX_APP_LIMIT_MODAL.CLOSE}
              </Button>
            </div>
          </ModalBody>

        </Modal>
      }
      <Breadcrumbs surface="light">
        <BreadcrumbItem>
          <Link
            data-testid="cvAppSub_camerasLinkToDevices"
            to="/devices"
            className="breadcrums-item_link"
          >
            Cameras
          </Link>
        </BreadcrumbItem>
        {state &&
        state.hasOwnProperty("action") &&
        state?.action === "cameraInfo" ? (
          <BreadcrumbItem>
            <Link
              data-testid="cvAppSub_camerasDetailsLinkTocameraInfoPage"
              to={{
                pathname: "/cameraInfoPage",
                search: `?${createSearchParams({
                  cameraId: state?.cameradata?.id,
                })}`,
              }}
              state={{ data: state?.cameradata ? state?.cameradata : {} }}
              className="breadcrums-item_link"
            >
              Camera Details
            </Link>
          </BreadcrumbItem>
        ) : state &&
          state.hasOwnProperty("action") &&
          state?.action === "cameraOnBoarding" &&
          state?.cameradata ? (
          <>
            {state &&
            state.hasOwnProperty("originPage") &&
            state?.originPage === ORIGIN_PAGES.CAMERA_DETAILS ? (
              <BreadcrumbItem>
                <Link
                  data-testid="cvAppSub_camerasDetailsLinkTocameraInfoPage"
                  to={{
                    pathname: "/cameraInfoPage",
                    search: `?${createSearchParams({
                      cameraId: state?.cameraId,
                    })}`,
                  }}
                  state={{
                    data: state?.cameradata?.find(
                      (item) => item?.id === state?.cameraId
                    )
                      ? state?.cameradata?.find(
                          (item) => item?.id === state?.cameraId
                        )
                      : {},
                  }}
                  className="breadcrums-item_link"
                >
                  {
                    state?.cameradata?.find(
                      (item) => item?.id === state?.cameraId
                    )?.name
                  }
                </Link>
              </BreadcrumbItem>
            ) : camInfo ? (
              <BreadcrumbItem>
                <Link
                  data-testid="cvAppSub_camerasDetailsLinkTocameraInfoPage"
                  to={{
                    pathname: "/cameraInfoPage",
                    search: `?${createSearchParams({
                      cameraId: state?.cameraId,
                    })}`,
                  }}
                  className="breadcrums-item_link"
                >
                  {cameraName}
                </Link>
              </BreadcrumbItem>
            ) : (
              <></>
            )}
            <BreadcrumbItem>
              <Link
                data-testid="cvAppSub_licensesLinkTocvAppsLicenseType"
                to="/cvAppsLicenseType"
                state={{
                  FOVImages: state?.FOVImages,
                  cameraId: state.cameraId,
                  companionId: state.companionId,
                  fieldOfViews: state.fieldOfViews,
                  FOVNames: state.FOVNames,
                  FOVImageURL: state.FOVImageURL,
                  FOVId: state.FOVId,
                  action: "create",
                  siteSelected: state?.siteSelected,
                  floorSelected: state?.floorSelected,
                  areaSelected: state?.areaSelected,
                  subAreaSelected: state?.subAreaSelected,
                  cameraname: state?.cameraname,
                  fromAction: state?.action,
                  cameradata: state?.cameradata,
                  sku: state?.sku,
                  camInfo: state?.camInfo,
                  roiList: cvAppList,
                  originPage: state?.originPage ? state?.originPage : "",
                }}
                className="breadcrums-item_link"
              >
                Licenses
              </Link>
            </BreadcrumbItem>
          </>
        ) : state &&
          state.hasOwnProperty("action") &&
          state?.action === "cameraPairing" ? (
          <BreadcrumbItem>
            <Link
              data-testid="cvAppSub_cameraPairingLinkTocameraOnboarding"
              to="/cameraOnboarding"
              state={{
                camName: state?.cameraId,
                breadcrumbItem: "Camera Pairing",
                isPairing: true,
              }}
              className="breadcrums-item_link"
            >
              Camera Pairing
            </Link>
          </BreadcrumbItem>
        ) : state &&
          state.hasOwnProperty("originPage") &&
          state?.originPage === ORIGIN_PAGES.CAMERA_DETAILS ? (
          <BreadcrumbItem>
            <Link
              data-testid="cvAppSub_cameraNameLinkTocameraInfoPage"
              to={{
                pathname: "/cameraInfoPage",
                search: `?${createSearchParams({ cameraId: state?.cameraId })}`,
              }}
              state={{
                data: state?.cameradata?.find(
                  (item) => item?.id === state?.cameraId
                )
                  ? state?.cameradata?.find(
                      (item) => item?.id === state?.cameraId
                    )
                  : {},
              }}
              className="breadcrums-item_link"
            >
              {
                state?.cameradata?.find((item) => item?.id === state?.cameraId)
                  ?.name
              }
            </Link>
          </BreadcrumbItem>
        ) : (
          <></>
        )}
        <BreadcrumbItem>CV App Configuration</BreadcrumbItem>
      </Breadcrumbs>
      <div className="cvappsub-page-title">
        <Title size="medium" id="cvappsub-page-title">{state?.cameraname}</Title>
      </div>
      <br />
      <br />
      {fovS.map((fov, index) => (
        <div key={index}>
          <Section
            title={`Select the apps that you like to subscribe to for:  ${fov}`}
            fovtitles={fovS[fov]}
            floorImage={
              fovsArrImg && fovsArrImg.length
                ? fovsArrImg.find((obj) => obj.name === fov)?.url || ""
                : ""
            }
            loading={loading}
            snapshotImg={SnapshotImg}
            sku={currentSku ? currentSku : getFovLicenseSku(fov)}
            uniqueDisplayNames={uniqueDisplayNames}
            selectedNames={selectedNames}
            selectedSubCategoryNames={selectedSubCategoryNames}
            setShowLicenseLimitBanner={setShowLicenseLimitBanner}
            setShowModal={setShowModal}
            setIsNextDisable={setIsNextDisable}
            selectedCvAppName={selectedCvAppName}
            selectedCVAppsWithCount={selectedCVAppsWithCount}
            licenseStartedCount={licenseStartedCount}
            licenseList={licenseList}
            selectedCount={selectedCvAppName?.filter((n) => !checkedCvAppid.includes(n))?.length || 0}            
            isFetchingPairedImaged={isFetchingPairedImaged}            
            cvAppListFromReducer={cvAppListFromReducer}
            cameraParentCCDInfo={cameraParentCCDInfo}
            onOptionChange={(e, option, category, floorImage, sku) =>
              handleOptionChange(
                fov,
                e,
                option,
                category,
                floorImage,
                sku,
                licenseList,
                getLicenseTypeDisplayed
              )
            }
          />
          {index < fovS.length - 1 && <hr className="section-divider" />}
        </div>
      ))}
      <div className="mt-3">
        <Button
          className="cvappsub-btn-back"
          size={buttonSize}
          data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPP_SUB_BACK_BUTTON_GROUP_BUTTON}
          onClick={handleBackButtonClick}
        >
          Back
        </Button>

        <Button
          className="cvappsub-btn-next"
          size={buttonSize}
          disabled={disbale_NextButton(isNextDisable)}
          data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPP_SUB_NEXT_BUTTON_GROUP_BUTTON}
          onClick={handleNextButtonClick}
        >
          Next
        </Button>
      </div>
      <Modal
        opened={showCalibrationConfirmModal}
        disableOutsideClick={true}
        hideCloseButton={true}
        closeButton={
          <div
            className="camera-info-modal-close-btn"
            onClick={closeCalibrationConfirmModal}
          >
            <Icon name="close" />
          </div>
        }
      >
        <ModalBody>
          {pendingCalibrationApp?.calibrationInProgress ? (
            <Notification
              type="info"
              hideCloseButton={true}
              title={`${pendingCalibrationApp?.title ? `${pendingCalibrationApp.title}: ` : ""}${TITLES.CALIBRATION_IN_PROGRESS_MODAL}`}
              subtitle={`${pendingCalibrationApp?.name || "This app"} ${SUBTITLES.CALIBRATION_IN_PROGRESS_DETAIL}`}
            />
          ) : (
            <Notification
              type="warning"
              hideCloseButton={true}
              title="Manual calibration required"
              subtitle={`${pendingCalibrationApp?.name || "This app"} requires manual stream calibration. Please calibrate now to proceed.`}
            />
          )}
        </ModalBody>
        <ModalFooter
          buttonData={
            pendingCalibrationApp?.calibrationInProgress
              ? {
                  primary: {
                    width: "100%",
                    children: BUTTON_LABELS.OK,
                    "data-testid": "cvAppSub_calibrationInProgressOkButton",
                    onClick: closeCalibrationConfirmModal,
                  },
                }
              : {
                  primary: {
                    width: "100%",
                    children: "Yes",
                    "data-testid": "cvAppSub_calibrationConfirmNoButton",
                    onClick: handleCalibrationConfirmYes,
                  },
                  close: {
                    width: "100%",
                    children: "No",
                    "data-testid": "cvAppSub_calibrationConfirmYesButton",
                    onClick: closeCalibrationConfirmModal,
                  },
                }
          }
        />
      </Modal>
      <BaseModal
        opened={showStreamCalibrationModal}
        disableOutsideClick={true}
        hideCloseButton={showManualCalibration}
        onClose={closeStreamCalibrationModal}
        closeButton={
          <div
            className="camera-info-modal-close-btn"
            data-testid="cvAppSub_calibrationModalClose"
            onClick={closeStreamCalibrationModal}
          >
            <Icon name="close" />
          </div>
        }
        width="70vw"
        height="950px"
        maxWidth="none"
        // BaseModal footer is positional: left = filled button (closeButtonText/onBack),
        // right = outlined button (primaryButtonText/onCloseButtonClick).
        closeButtonText={BUTTON_LABELS.CALIBRATE}
        onBack={() => setHandleNextCalibrationSteps(true)}
        backdisabled={isCalibrationLoading || isCalibrationNextDisabled}
        primaryButtonText={BUTTON_LABELS.CANCEL}
        onCloseButtonClick={closeStreamCalibrationModal}
        disabled={isCalibrationLoading}
      >
        <Loader active={isCalibrationLoading} fullscreen={false} surface="light" />
        {/* Keeps the content below the close icon so it isn't clipped by the dark panel */}
        {!showManualCalibration && <div style={{ height: 24 }} />}
        {pendingCalibrationFov && (
          <StreamCalibrationModal
            fovDatas={calibrationFovDatas}
            companionDevice={cameraParentCCDInfo}
            handleNextCalibrationSteps={handleNextCalibrationSteps}
            setHandleNextCalibrationSteps={setHandleNextCalibrationSteps}
            onNextDisableChange={setIsCalibrationNextDisabled}
            onClose={closeStreamCalibrationModal}
            setShowManualCalibration={setShowManualCalibration}
            manualCount={manualCalibrationCount}
            setmanualCount={setManualCalibrationCount}
            manualDevices={manualCalibrationDevices}
            setManualDevices={setManualCalibrationDevices}
            showManualBack={showManualCalibrationBack}
            setShowManualBack={setShowManualCalibrationBack}
            setIsLoading={setIsCalibrationLoading}
            onCalibrationComplete={handleCalibrationComplete}
            forceManual={true}
            isHistoricalSearchEligible={false}
          />
        )}
      </BaseModal>
      <ChangeLicenseModal
        isOpen={isChangeLicenseModalVisible}
        onClose={() => {
          setIsChangeLicenseModalVisible(false);
          setPendingLimitApp(null);
        }}
        onConfirm={handleChangeLicenseConfirm}
        currentSku={currentSku}
        licenseList={licenseList}
        roiList={roiList}
        deviceId={deviceId}
        upgradeMode
        primaryButtonLabel="Yes, upgrade"
        title={LICENSE_MODAL_TEXTS.UPGRADE_LICENSE}
        infoMessage={pendingLimitApp?.name === CVAPP_OBJECTBLUR || pendingLimitApp?.name === CVAPP_CAMERAADJUSTMENT
          ? licensesMessage.STARTER_EXTRA_FEATURE_NOT_SUPPORTED(pendingLimitApp?.name)
          : ""} 
        onUnsubscribeClick={() => {
          setIsChangeLicenseModalVisible(false);
          navigateToUnsubscribeRoi(deviceId, true);
        }}
      />
    </div>
  );
};

const Section = ({
  title,
  floorImage,
  selectedCount,
  onOptionChange,
  sku,
  selectedCvAppName,
  licenseList,
  licenseStartedCount,
  isFetchingPairedImaged,
  selectedCVAppsWithCount,
  snapshotImg: SnapshotImg,
  cvAppListFromReducer,
  cameraParentCCDInfo,
}) => {

  const cvAppList = cvAppListFromReducer || [];
  const sectionHeader = title.split(":")[0];
  const sectionTitle = title.split(":")[1];
  const cameraHasInHouse = isCcdHasInHouseVersion(cameraParentCCDInfo);
  const targetVersion = cameraHasInHouse ? "2" : "1";
  const obj = cvAppList
    // 1. Filter first so we don't waste time mapping items we don't need
    .filter((cvapp) => {
      const version = cvapp.version ?? "1";
      return version === targetVersion;
    })
    // 2. Map second, returning a NEW object to prevent mutating cvAppList
    .map((m) => ({
      ...m, // Copy all existing properties safely
      displayName: m.resources?.displayName,
      name: m.resources?.displayNameFull || m.name
    }));

  var categorizedObj = obj && Object.groupBy(obj, ({ category }) => category);
  var result = {};
  categorizedObj && Object.keys(categorizedObj).forEach((element) => {
    result[element] = Object.groupBy(
      categorizedObj[element],
      ({ displayName }) => displayName
    );
  });

  function reorderCategories(result) {
    const order = ["Statistics Apps", "Event Apps"];
    const reordered = {};

    // First, add the categories in the specified order
    order.forEach((category) => {
      if (result[category]) {
        reordered[category] = result[category];
      }
    });

    // Then add any remaining categories
    Object.keys(result).forEach((category) => {
      if (!reordered[category]) {
        reordered[category] = result[category];
      }
    });
    return reordered;
  }

  const orderedResult = reorderCategories(result);
  const totalSelectedCount = Math.min(licenseStartedCount, 3);

  return (
    <div>
      <div className="cvappsub-section-description">
        <Title size="small" className="cvappsub-section-title">
          {`${sectionHeader}:${sectionTitle}`}
        </Title>
      </div>
      <div className="section-container">
        <div className="image-container">
          {SnapshotImg && (
            <SnapshotImg className="cvappsub-snapshot" />
          )}
          {(() => {
            const matchingLicense = licenseList.find((l) => sku === l.sku);
            if (!matchingLicense) return null;
            return (
              <p className="image-title" key={matchingLicense.displayName}>
                <span className="cvappsub-license-label">
                  {matchingLicense.displayName} license
                </span>{" "}
                (
                {matchingLicense.displayName === LICENSE_TYPES.STARTER
                  ? `${totalSelectedCount}/3`
                  : licenseStartedCount + selectedCount}{" "}
                Apps selected)
              </p>
            );
          })()}
          {isFetchingPairedImaged.length > 0 && (
            <p className="image-title">
              <pre>{isFetchingPairedImaged}</pre>
            </p>
          )}
        </div>

        {/* Render each Apps in a separate column */}
        {Object.keys(orderedResult).map((category) => {
          var categoryApp = orderedResult[category];
          return (
            <div key={category} className="apps-category">
              <p>
                <b
                  className="cvappsub-category-heading"
                >
                  {category}
                </b>
              </p>
              <div>
                {arrangeAsAlphabeticalOrder(Object.keys(categoryApp)).map(
                  (item) => {
                    var CvApOPbj = categoryApp[item];
                    var count = selectedCVAppsWithCount.filter(
                      (item) => item === CvApOPbj[0].displayName
                    ).length;
                    const disableIfOneExtraFeatureDeployed =
                      (CvApOPbj[0].displayName === CVAPP_OBJECTBLUR && selectedCVAppsWithCount.includes(CVAPP_OBJECTBLUR)) ||
                      (CvApOPbj[0].displayName === CVAPP_CAMERAADJUSTMENT && selectedCVAppsWithCount.includes(CVAPP_CAMERAADJUSTMENT));
                    return CvApOPbj.length == 1 ? (
                        <div
                          key={CvApOPbj[0].id}
                          className="apps-option cvappsub-single-app-row"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCvAppName?.includes(CvApOPbj[0].name)}
                            disabled={
                              (licenseStartedCount >= 4 &&
                                getLicenseTypeDisplayed(sku, licenseList) ===
                                  LICENSE_TYPES.STARTER &&
                                !selectedCVAppsWithCount.includes(
                                  CvApOPbj[0].displayName
                                ) &&
                                !selectedCvAppName.includes(
                                  CvApOPbj[0].displayName
                                )) ||
                                disableIfOneExtraFeatureDeployed
                            }
                            data-testid="cvAppSub_checkboxInputROI"
                            onChange={(e) =>
                              onOptionChange(
                                e,
                                CvApOPbj[0],
                                category,
                                floorImage,
                                sku
                              )
                            }
                          />{" "}
                          {CvApOPbj[0].displayName}{" "}
                          {selectedCVAppsWithCount.includes(
                            CvApOPbj[0].displayName
                          ) && (
                            <Badge
                              fillColor="black"
                              kind="numbered"
                              size="small"
                              leadingCharacter="false"
                              trailingText="false"
                              border="true"
                            >
                              {" "}
                              {selectedCVAppsWithCount[CvApOPbj[0].displayName]}
                              {`${count} ${count > 1 ? "ROIs" : "ROI"}`}
                            </Badge>
                          )}                        
                          {disableIfOneExtraFeatureDeployed && (
                            <span
                              style={{
                                verticalAlign: "sub",
                                textWrap: "wrap",
                                marginLeft: "5px",
                                marginTop: "5px"
                              }}
                            >
                              <Tooltip
                                title={CvApOPbj[0].displayName}
                              children={licensesMessage.EXTRAFEATURE_TOOLTIP}
                              />
                            </span>
                          )}
                        </div>
                    ) : (
                      <div key={item} className="cvappsub-multi-app-group">
                        <p className="cvappsub-subdisplay-name">
                          {CvApOPbj[0].displayName}
                        </p>
                        {CvApOPbj.map((app, index) => {
                          var count = selectedCVAppsWithCount.filter(
                            (item) => item === app.name
                          ).length;
                          const isVehicle =
                            app.name.toLowerCase().includes(CVAPP_VEHICLE_KEYWORD) ||
                            app.name.toLowerCase() === CVAPP_PARKINGOCCUPANCYOA.toLowerCase() ||
                            app.name.toLowerCase() === CVAPP_PARKINGOCCUPANCYEE.toLowerCase();
                          const isEntryExit = app.name.includes(CVAPP_ENTRY_EXIT_KEYWORD);
                          const tooltipContent = isEntryExit
                            ? (isVehicle ? CVAPP_TOOLTIP_VEHICLE_EE : CVAPP_TOOLTIP_PEOPLE_EE)
                            : (isVehicle ? CVAPP_TOOLTIP_VEHICLE_OA : CVAPP_TOOLTIP_PEOPLE_OA);
                          const tooltipTitle = isEntryExit ? `Entry/Exit Counting` : "Fixed area Counting";
                          return (
                            <div
                              key={app.id + "" + index} className="apps-option cvappsub-sub-app-row"
                            >
                              <div className="cvappsub-sub-app-inner">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={selectedCvAppName?.includes(app.name)}
                                    disabled={
                                      !selectedCvAppName.includes(app.name) &&
                                      licenseStartedCount >= 4 &&
                                      getLicenseTypeDisplayed(
                                        sku,
                                        licenseList
                                      ) === LICENSE_TYPES.STARTER &&
                                      !selectedCVAppsWithCount.includes(
                                        app.name
                                      ) &&
                                      !selectedCvAppName.includes(app.name)
                                    }
                                    id={app.subcategory}
                                    data-testid="cvAppSub_checkboxInputROI"
                                    onChange={(e) => {
                                      onOptionChange(
                                        e,
                                        app,
                                        category,
                                        floorImage,
                                        sku
                                      );
                                    }}
                                  />
                                </label>{" "}
                                {app.subcategory}{" "}
                                {selectedCVAppsWithCount.includes(app.name) ? (
                                  <Badge
                                    fillColor="black"
                                    kind="numbered"
                                    size="small"
                                    leadingCharacter="false"
                                    trailingText="false"
                                    border="true"
                                  >
                                    {selectedCVAppsWithCount[app.name]}
                                    {`${count} ${count > 1 ? "ROIs" : "ROI"}`}
                                  </Badge>
                                ) : (
                                  <span className="cvappsub-tooltip-span">
                                    <Tooltip title={tooltipTitle} children={tooltipContent}></Tooltip>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default CVAppSub;
